import { useEffect, useMemo, useState } from 'react';
import {
  getStoredTablePageSize,
  setStoredTablePageSize
} from 'components/common/table/tablePageSizeStorage';

const DEFAULT_PAGE_SIZE = 10;

const clampPageIndex = (pageIndex, pageCount) => {
  if (!pageCount) {
    return 0;
  }

  return Math.min(Math.max(pageIndex, 0), pageCount - 1);
};

const useTableControlPagination = (
  items,
  initialPageSize = DEFAULT_PAGE_SIZE,
  pageSizeStorageKey
) => {
  const [pageSize, setPageSizeState] = useState(() =>
    getStoredTablePageSize(initialPageSize, pageSizeStorageKey)
  );
  const [pageIndex, setPageIndex] = useState(0);
  const rowCount = items?.length || 0;
  const pageCount = rowCount > 0 ? Math.ceil(rowCount / pageSize) : 0;
  const safePageIndex = clampPageIndex(pageIndex, pageCount);

  useEffect(() => {
    setPageIndex(0);
  }, [rowCount, pageSize]);

  useEffect(() => {
    const storedPageSize = getStoredTablePageSize(
      initialPageSize,
      pageSizeStorageKey
    );

    setPageSizeState(storedPageSize);
  }, [initialPageSize, pageSizeStorageKey]);

  useEffect(() => {
    if (safePageIndex !== pageIndex) {
      setPageIndex(safePageIndex);
    }
  }, [pageIndex, safePageIndex]);

  const pageRows = useMemo(() => {
    const start = safePageIndex * pageSize;
    return (items || []).slice(start, start + pageSize);
  }, [items, pageSize, safePageIndex]);

  const gotoPage = nextPageIndex => {
    setPageIndex(clampPageIndex(nextPageIndex, pageCount));
  };

  const setPageSize = nextPageSize => {
    const safePageSize = Number(nextPageSize);

    if (Number.isFinite(safePageSize) && safePageSize > 0) {
      setStoredTablePageSize(safePageSize, initialPageSize, pageSizeStorageKey);
      setPageSizeState(safePageSize);
    }
  };

  return {
    canNextPage: safePageIndex < pageCount - 1,
    canPreviousPage: safePageIndex > 0,
    gotoPage,
    nextPage: () => gotoPage(safePageIndex + 1),
    pageCount,
    pageIndex: safePageIndex,
    pageRows,
    pageSize,
    previousPage: () => gotoPage(safePageIndex - 1),
    rowCount,
    setPageSize
  };
};

export default useTableControlPagination;
